import { Injectable } from "@angular/core"
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http"

import { AccountUser } from "@app/modules/usuario/v2/interface/AccountUser"
import { environment } from "../../../../environments/environment"
import { User } from "../models/user/user"
import { UserInformationRequest } from "../models/user/user-information-request"
import {
  CustomURLEncoder,
  isNullOrUndefined,
} from "src/app/core/helpers/http-params-extensions"
import { IPostUserExportOnboardingPayload } from "src/app/shared/interfaces/requests"
import { IUserExportOnboardingResponseData } from "../../../shared/interfaces/responses"
import { HEADERS } from "./const/http-header-interception"
import { map, Observable, of, shareReplay, tap } from "rxjs"
import { Account } from "../models/user/account"
import { Profiles } from "@app/modules/usuario/v2/interface/profiles"
import { Family } from "@app/modules/usuario/v2/interface/family"
import { TemporaryPwdRequest } from "@app/modules/usuario/v2/interface/temporary-pwd-request"
import { UserProfile } from "@app/modules/usuario/v2/enums/UserRole"
import { IUser } from "@app/shared/interfaces/user"

@Injectable({
  providedIn: "root",
})
export class AccountService {
  private post_export_pdf_url: string
  private post_account_url: string
  private put_account_url: string
  private get_account_id_url: string
  private get_account_paginate_url: string
  private get_account_autocomplete_url: string
  private get_profile_all: string
  private get_account_informations_id_url: string
  private post_resend_access_email: string
  private post_resend_batch_access_email: string
  private get_temporary_password: string
  private post_invitation_lex_account: string
  private post_user_export_onboarding: string
  private get_account_verification_link: string
  private get_field_restriction: string
  private get_field_restriction_migrated: string
  private get_user_paginate: string
  private post_send_pendings: string
  private put_integration_economic_group: string
  private put_unlink_academic_email: string
  private post_email_recovery_password: string
  private post_family_url: string
  private put_family_url: string
  private get_family_url: string
  private getUserInformationsCache = new Map<string, Observable<any>>()
  private get_merge_candidates_url: string
  private post_merge_preview_url: string
  private post_merge_url: string

  // "private http: HttpClient":
  // - this autodeclares a private "http: HttpClient" with the argument's value
  constructor(private httpClient: HttpClient) {
    const userUrl = environment.endpointUser.url
    const accountUrl = environment.endpointAccount.url
    const impersonateUrl = environment.endpointImpersonate.url
    const fieldRestrictionUrl = environment.endpointFieldRestriction.url

    const routes = {}

    for (const userRoute in environment.endpointUser.routes) {
      routes[userRoute] = userUrl + environment.endpointUser.routes[userRoute]
    }

    for (const accountRoute in environment.endpointAccount.routes) {
      if (!routes[accountRoute]) {
        routes[accountRoute] =
          accountUrl + environment.endpointAccount.routes[accountRoute]
      }
    }

    this.post_export_pdf_url =
      environment.endpointBackofficeGateway.url +
      environment.endpointBackofficeGateway.routes.post_export_pdf
    this.post_account_url = routes["post_account"]
    this.put_account_url = routes["put_account"]
    this.get_account_id_url = routes["get_account_id"]
    this.get_account_paginate_url = routes["get_account_paginate"]
    this.get_profile_all = routes["get_profile_all"]
    this.get_account_autocomplete_url = routes["get_account_autocomplete"]
    this.get_account_informations_id_url = routes["get_account_informations_id"]
    this.post_resend_access_email = routes["post_resend_access_email"]
    this.post_resend_batch_access_email =
      routes["post_resend_batch_access_email"]
    this.get_temporary_password =
      impersonateUrl + environment.endpointImpersonate.routes.impersonate
    this.post_invitation_lex_account = routes["post_invitation_lex_account"]
    this.post_user_export_onboarding = routes["post_user_export_onboarding"]
    this.get_account_verification_link = routes["get_account_verification_link"]
    this.get_field_restriction =
      fieldRestrictionUrl +
      environment.endpointFieldRestriction.routes.field_restriction
    this.get_field_restriction_migrated =
      fieldRestrictionUrl +
      environment.endpointFieldRestriction.routes.migrated_field_restriction
    this.post_send_pendings = routes["post_send_pendings"]
    this.put_integration_economic_group =
      routes["put_integration_economic_group"]
    this.put_unlink_academic_email = routes["put_unlink_academic_email"]
    this.post_email_recovery_password = routes["post_email_recovery_password"]
    this.post_family_url = routes["post_family"]
    this.put_family_url = routes["put_family"]
    this.get_family_url = routes["get_family"]
    this.get_merge_candidates_url = routes["get_merge_candidates"]
    this.post_merge_preview_url = routes["post_merge_preview"]
    this.post_merge_url = routes["post_merge"]
  }

  postUser(account: Account) {
    return this.httpClient.post(this.post_account_url, account, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  putUser(account: Account) {
    return this.httpClient.put(
      this.put_account_url.replace("{accountId}", account.id),
      { status: true, ...account },
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getAutoCompleteUser(value: string) {
    const params = new HttpParams({ encoder: new CustomURLEncoder() }).set(
      "term",
      value
    )
    return this.httpClient.get(this.get_account_autocomplete_url, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }

  getAllProfile(getAdmin: boolean, getOnlyCollabsProfile = false) {
    const params = new HttpParams({ encoder: new CustomURLEncoder() })
      .set("getAdmin", String(getAdmin))
      .set("getOnlyCollaborator", String(getOnlyCollabsProfile))
    return this.httpClient
      .get<Profiles>(this.get_profile_all, {
        params,
        headers: new HttpHeaders(HEADERS),
      })
      .pipe(
        map((profiles) => {
          return profiles.map((profile) => {
            return {
              ...profile,
              profileEnum: UserProfile[profile.type],
            }
          })
        })
      )
  }

  postFamily(account: AccountUser) {
    return this.httpClient.post(this.post_family_url, account)
  }

  putFamily(account: AccountUser) {
    return this.httpClient.put(`${this.put_family_url}/${account.id}`, account)
  }

  testEditUser() {
    const tempUser: AccountUser = {
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      tenantId: "tenant123",
      phone: {
        ddi: "+1",
        value: "1234567890",
      },
      classes: [
        {
          id: "class1",
          profileIds: ["profile1"],
          subjectIds: ["subject1", "subject2"],
          courseId: "course1",
        },
      ],
      profileTypes: [
        /* "c006a0b5-8613-49ef-9d49-35a12b111d7a", */ "683F31C0-F806-4EBA-9CA7-AB23798A585F".toLocaleLowerCase() /* "bb27985e-4752-420b-a54e-62b45f9056fd", "31e26431-acf8-4de9-ae93-78b8f51a19a2" */,
      ],
      document: {
        type: 1,
        value: "123456789",
      },
      gender: "male",
      referenceId: "ref123",
      status: true,
      birthDate: new Date("1990-01-01"),
      parents: [],
      children: [],
      id: "user123",
      registrationOrigin: 1,
    }
    return of(tempUser)
  }

  getUsers(model: User) {
    let params = new HttpParams({ encoder: new CustomURLEncoder() })

    if (!isNullOrUndefined(model.AskPage)) {
      params = params.set("askPage", String(model.AskPage))
    }

    if (!isNullOrUndefined(model.ItensPerPage)) {
      params = params.set("itensPerPage", String(model.ItensPerPage))
    }

    if (!isNullOrUndefined(model.OrderByFilter)) {
      params = params.set("orderByFilter", String(model.OrderByFilter))
    }

    if (!isNullOrUndefined(model.OrderByDirection)) {
      params = params.set("isDesc", String(model.isDesc))
    }

    params = params.set(
      "classFilterType",
      String(model.ClassFilterType || "all")
    )

    if (!isNullOrUndefined(model.UserLogin)) {
      params = params.set(
        "userLogin",
        model.UserLogin || model.UserLoginOrNameOrCpf
      )
    }

    if (!isNullOrUndefined(model.UserName)) {
      params = params.set(
        "userName",
        String(model.UserName || model.UserLoginOrNameOrCpf)
      )
    }

    if (!isNullOrUndefined(model.ProfileId)) {
      params = params.set("profileId", String(model.ProfileId))
    }

    if (model.ClassId && model.ClassId.length > 0) {
      for (let i = 0, j = model.ClassId.length; i < j; ++i) {
        params = params.append(`classId[${i}]`, model.ClassId[i])
      }
    }

    if (!isNullOrUndefined(model.PermissionId)) {
      params = params.set("permissionId", String(model.PermissionId))
    }

    if (!isNullOrUndefined(model.UserStatus)) {
      const userStatusString = String(model.UserStatus)
      if (userStatusString === "active" || userStatusString === "inactive") {
        params = params.set(
          "status",
          userStatusString === "active" ? "true" : "false"
        )
      }
    }
    if (
      !isNullOrUndefined(model.UserLoginOrNameOrCpf) &&
      String(model.UserLoginOrNameOrCpf).length > 0
    ) {
      params = params.set(
        "userLoginOrNameOrCpf",
        String(model.UserLoginOrNameOrCpf)
      )
    }
    if (
      !isNullOrUndefined(model.MigrationStatus) &&
      String(model.MigrationStatus).length > 0
    ) {
      params = params.set("migrationStatus", String(model.MigrationStatus))
    }

    if (
      !isNullOrUndefined(model.PendingStatus) &&
      String(model.PendingStatus).length >= 0
    ) {
      params = params.set("pendingStatus", String(model.PendingStatus))
    }

    return this.httpClient.get(this.get_account_paginate_url, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }

  postUserExportOnboarding(data: IPostUserExportOnboardingPayload) {
    return this.httpClient
      .post<IUserExportOnboardingResponseData>(
        this.post_user_export_onboarding,
        data,
        { headers: new HttpHeaders(HEADERS) }
      )
      .toPromise()
  }

  getUserAccountVerificationLink(userId: string) {
    return this.httpClient
      .get<{ link: string }>(
        this.get_account_verification_link.replace("{userId}", userId),
        { headers: new HttpHeaders(HEADERS) }
      )
      .toPromise()
  }

  getOne(userId: string) {
    return this.httpClient.get(
      this.get_account_id_url.replace("{accountId}", userId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getUserInformations(userId: string, model?: UserInformationRequest) {
    if (!model && this.getUserInformationsCache.has(userId)) {
      return this.getUserInformationsCache.get(userId)
    }
    const request$ = this.httpClient.get(
      this.get_account_informations_id_url.replace("{accountId}", userId),
      { headers: new HttpHeaders(HEADERS) }
    ).pipe(shareReplay(1))
    if (!model) {
      this.getUserInformationsCache.set(userId, request$)
    }
    return request$
  }

  getUserInformationsWithoutCache(userId: string): Observable<IUser> {
    return this.httpClient.get<IUser>(
      this.get_account_informations_id_url.replace("{accountId}", userId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  clearUserInformationsCache(userId?: string) {
    if (userId) {
      this.getUserInformationsCache.delete(userId)
    } else {
      this.getUserInformationsCache.clear()
    }
  }

  requestAccessEmail(userId: string) {
    return this.httpClient.post(
      this.post_resend_access_email
        .replace("{userId}", userId)
        .replace("{forceSendEmail}", "true"),
      {},
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  resendBatchAccessEmail(userIds: string[]) {
    return this.httpClient.post(this.post_resend_batch_access_email, userIds, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  getTemporaryPassword(userId: string) {
    return this.httpClient.get<TemporaryPwdRequest>(
      this.get_temporary_password.replace("{userId}", userId),
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  postSendInvitations(userIds: string[]) {
    return this.httpClient.post(
      this.post_invitation_lex_account,
      {
        ids: userIds,
      },
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getFieldsRestriction(userId: string) {
    const url = this.get_field_restriction_migrated

    return this.httpClient.get(url.replace("{userId}", userId), {
      headers: new HttpHeaders(HEADERS),
    })
  }

  postSendEmailComplements(usersIds: string[]) {
    return this.httpClient.post(this.post_send_pendings, usersIds, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  postSendEmailRecoveryPassword(usersIds: string[]) {
    return this.httpClient.post(this.post_email_recovery_password, usersIds, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  postExportPdf(
    userId: string,
    generateNewCredentials: boolean,
    classId?: string
  ) {
    const params = { generateNewCredentials, classId }
    const url = `${this.post_export_pdf_url}${userId}`
    return this.httpClient.post(url, params, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  putIntegrationEconomicGroup(
    willBeManagerCurrent,
    willBeManagerLast,
    tenantId,
    serviceId
  ) {
    return this.httpClient.put(
      this.put_integration_economic_group,
      {
        WillBeManagerCurrent: willBeManagerCurrent,
        WillBeManagerLast: willBeManagerLast,
        TenantId: tenantId,
        ServiceIdLast: serviceId,
      },
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  putUnlinkAcademicEmail(userId: string) {
    return this.httpClient.put(
      this.put_unlink_academic_email.replace("{userId}", userId),
      { status: true },
      { headers: new HttpHeaders(HEADERS) }
    )
  }

  getFamily(userId: string) {
    return this.httpClient.get<Family>(this.get_family_url + `/${userId}`, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  getMergeCandidates(cpf: string, email: string) {
    let params = new HttpParams({ encoder: new CustomURLEncoder() })
    if (cpf) params = params.set("cpf", cpf)
    if (email) params = params.set("email", email)
    return this.httpClient.get<any>(this.get_merge_candidates_url, {
      params,
      headers: new HttpHeaders(HEADERS),
    })
  }

  postMergePreview(payload: any) {
    return this.httpClient.post<any>(this.post_merge_preview_url, payload, {
      headers: new HttpHeaders(HEADERS),
    })
  }

  postMerge(payload: any) {
    return this.httpClient.post<any>(this.post_merge_url, payload, {
      headers: new HttpHeaders(HEADERS),
    })
  }
}
